import React, { useState, useEffect } from "react";
import axios from "axios";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import moment from "moment";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useData } from "../hooks/useData";
import Success from "./Success";
import RegistrationForm from "./RegistrationForm";
import { dateToDbUtcIsoString, isDateValid } from "../utils/datetime";
import { useTranslation } from "react-i18next";

import "core-js/stable";
import "regenerator-runtime/runtime";

axios.defaults.withCredentials = true;
axios.defaults.xsrfHeaderName = "X-CSRFTOKEN";
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.headers.common["Pragma"] = "no-cache";
axios.defaults.headers.common["Cache-Control"] = "no-store, no-cache, no-transform, must-revalidate, max-age=0";

const PUB_V2_REG = "/public/v2/registration/";
const PUB_V2_ACTIVATE = "/public/v2/activate";

export const STAGES = {
  Error: -1,
  Loading: 0,
  Activate: 1,
  Spinner: 2,
  EnterPin: 3,
  SkipPin: 4,
  Done: 5,
};

const RegistrationFormContainer = () => {
  const { t } = useTranslation();
  const [pinError, setPinError] = useState("");
  const [formError, setFormError] = useState(null);
  const [stage, setStage] = useState(STAGES.Loading);
  const [initialStage, setInitialStage] = useState(STAGES.Activate);
  const [user, setUser] = useState({});
  const { executeRecaptcha } = useGoogleReCaptcha();

  const { responseData, isLoading, errors } = useData(PUB_V2_REG + window.location.search);
  const { custom_data: customData, style } = responseData;
  useEffect(() => {
    const { user: responseUser } = responseData;
    if (Object.keys(responseData).length > 1) {
      setInitialStage(responseUser.fields.id ? STAGES.SkipPin : STAGES.Activate);
      setStage(responseUser.fields.id ? STAGES.SkipPin : STAGES.Activate);
      setUser(responseUser);
    }
  }, [responseData]);

  const handleSummary = () => {
    if (user && user.fields) {
      if (!user.fields.is_active) {
        // in the case its undefined
        return stage === STAGES.Done ? t("registration_form_container.thank_you_signing_up") : style.create_instructions;
      } else {
        return stage === STAGES.Done ? t("registration_form_container.profile_updated") : style.update_instructions;
      }
    }
  };

  const summary = handleSummary();

  const datesToISOFormat = (fields, data = {}) => {
    let ret = { ...data };
    fields
      .filter((c) => c.is_extended && c.field_type === "date")
      .forEach((c) => (ret[c.internal_name] = data[c.internal_name] ? moment(data[c.internal_name]).toDate().toISOString() : ""));
    fields
      .filter((c) => c.is_extended && c.field_type === "utc-datetime")
      .forEach((c) => (ret[c.internal_name] = isDateValid(data[c.internal_name]) ? dateToDbUtcIsoString(new Date(data[c.internal_name])) : ""));
    return ret;
  };

  // For disabled/hidden fields due to opt-out override, we need to recover the values since disabled/hidden input values are discarded on submit
  const hydrateDisabledFields = (data) => {
    const currentComms = responseData?.communications || {};
    // If no communications are defined in the data, initialize it
    if (data.communications === undefined) data.communications = {};
    const newComms = data.communications;

    for (let commType in currentComms) {
      // If communication type is missing, initialize it
      if (!newComms[commType]) newComms[commType] = {};

      for (let commField in currentComms[commType]) {
        // If communication field is missing or is disabled, fill it with the current value
        if (newComms[commType][commField] === undefined || currentComms[commType][commField].disabled) {
          newComms[commType][commField] = currentComms[commType][commField].value;
        }
      }
    }
  };

  // Preserve admin_only field values
  const hydrateAdminOnlyFields = (data) => {
    const adminOnlyFields = customData.filter((field) => field.admin_only);

    adminOnlyFields.forEach((field) => {
      const existingValue = field.is_extended ? user.fields.additional_data?.[field.internal_name] : user.fields[field.internal_name];

      if (existingValue !== undefined && existingValue !== null && existingValue !== "") {
        if (field.is_extended) {
          if (!data.additionalData) data.additionalData = {};
          data.additionalData[field.internal_name] = existingValue;
        } else {
          if (!data.user) data.user = {};
          data.user[field.internal_name] = existingValue;
        }
      }
    });
  };

  const postSubmit = async (data) => {
    setFormError(null);

    hydrateAdminOnlyFields(data);

    // If safesignal enrollment is required by style, always submit as enrolling.
    if (responseData?.style?.require_safesignal_enrollment) {
      data.enrolling_in_safety = true;
    }

    data.user.additional_data = datesToISOFormat(customData, data.additionalData);
    delete data.additionalData;
    setPinError("");

    hydrateDisabledFields(data);
    if (stage === STAGES.Activate) {
      setStage(STAGES.Spinner);
      if (!executeRecaptcha) {
        console.warn("Execute reCAPTCHA not yet available");
        setStage(initialStage);
        return;
      }

      try {
        const action = "public_v2_activate";
        const recaptcha_token = await executeRecaptcha(action);
        await axios.post(PUB_V2_ACTIVATE + window.location.search, {
          ...data.user,
          recaptcha_token,
          action,
        });
        setStage(STAGES.EnterPin);
      } catch (err) {
        setStage(initialStage);
        handleApiError(err);
      }
    } else {
      try {
        await axios.post(PUB_V2_REG + window.location.search, data);
        setStage(STAGES.Done);
      } catch (err) {
        handleApiError(err);
      }
    }
  };

  const handleApiError = (e) => {
    console.warn(e);
    if (e.pin) {
      setPinError(e.pin);
      setStage(initialStage);
      return;
    } else {
      setPinError("");
    }
    const response = e.response?.data || [];
    setFormError(
      Object.entries(response).map(([k, v]) => {
        return {
          type: "api",
          name: k,
          message: v.join ? v.join(", ") : v,
        };
      }),
    );
    axios.defaults.headers.common["Cache-Control"] = "no-cache";
  };

  if (isLoading.current) {
    return <div className="loader">{t("common.loading")}</div>;
  }
  if (Object.keys(errors).length > 1) {
    return (
      <div>
        <p>{t("registration_form_container.error")}</p>
      </div>
    );
  }
  return (
    <>
      <Header name={responseData.customer_name} imageUrl={style.header_image_url} summary={summary} />
      {stage === STAGES.Done ? (
        <Success customerName={responseData.customer_name} isActive={user.fields.is_active} ncUrl={responseData.nc_url} />
      ) : (
        <RegistrationForm
          responseData={responseData}
          user={user}
          customData={customData}
          postSubmit={postSubmit}
          stage={stage}
          pinError={pinError}
          formError={formError}
          style={style}
          publicGroups={responseData.public_groups}
        />
      )}
      <Footer style={style} />
    </>
  );
};

export default RegistrationFormContainer;
